import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	computedFrom,
	customElement,
	observable,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { UserModel, UserService } from '~/api/user-service';
import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import { Notification } from '~/components/notification/notification';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel, Modifiable } from '~/models/form-model';
import { UserInfoTooltipUtils } from '~/utils/user-info-tooltip-utils';

const componentTagName = 'a-user';

const styles = {
	body: 'a-modifiedby__body',
	label: 'a-modifiedby__label',
	value: 'a-modifiedby__value',
	row: 'a-modifiedby__row',
	tooltip: 'a-modifiedby__tooltip',
	tooltipInner: 'a-modifiedby__tooltip__inner',
};

registerComponent(componentTagName, {
	name: 'User',
	requirePath: PLATFORM.moduleName('components/field/custom/user'),
	props: commonFieldProperties,
	isSelectable: true,
});

@autoinject
@customElement('a-user')
@useView(PLATFORM.moduleName('./user.html'))
export class UserField extends Field {
	@observable({ changeHandler: 'refTooltipElChanged' })
	protected readonly refTooltipEl?: HTMLElement;

	protected userModel: UserModel | undefined;

	protected isInitialized: boolean = false;

	@bindable
	protected isLoginNameInvisible: boolean = false;

	constructor(
		public element: Element,
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
		private userService: UserService,
		readonly i18n: I18N,
	) {
		super(Field.TYPE.TEXT, element, parentForm, eventAggregator);
		this.isReadonly = true;
	}

	public attached() {
		super.attached();
		if (this.fieldModel) {
			this.updateTooltip();
		} else {
			this.setTooltipTitle('');
		}
	}

	protected fieldModelChanged(fieldModel: FieldModel) {
		super.fieldModelChanged(fieldModel);
		this.updateTooltip();
	}

	protected updateTooltip() {
		if (this.fieldModel) {
			this.fieldModel.modifiable = Modifiable.FALSE;
			this.queryForModifiedByUser(this.fieldModel.value);
		}
	}

	/**
	 * Compute the display value.
	 * Used when the field is in non-edit mode.
	 */
	@computedFrom('fieldModel', 'fieldModel.value', 'value', 'userModel')
	get displayValue() {
		if (!this.fieldModel) {
			return '';
		}
		if (this.isInitialized) {
			$(this.refTooltipEl).tooltip('update');
		}

		let result = this.fieldModel.value;
		if (
			!this.isLoginNameInvisible &&
			this.userModel &&
			this.userModel.userLoginName
		) {
			result += ' - ' + this.userModel.userLoginName;
		}
		return result;
	}

	private setTooltipTitle(title: string) {
		UserInfoTooltipUtils.setTooltipTitle(this.refTooltipEl, title);
	}

	protected getTooltipContent(userModel: UserModel) {
		return UserInfoTooltipUtils.getDefaultUserInfoTooltipContent(userModel);
	}

	private queryForModifiedByUser(userId: string) {
		if (!userId || userId === '0') {
			this.userModel = undefined;
			this.setTooltipTitle(
				this.i18n.tr('modified-by.failed-to-fetch-user-info', {
					userId,
				}),
			);
			return;
		}
		this.userService.getUserInfo(userId).then(
			(userModel) => {
				if (!userModel || userModel.id === undefined) {
					this.userModel = undefined;
					return;
				}
				this.userModel = userModel;
				const tooltipContent = this.getTooltipContent(userModel);
				this.setTooltipTitle(tooltipContent);
			},
			(error) => {
				this.userModel = undefined;
				const failedMessage = this.i18n.tr(
					'modified-by.failed-to-fetch-user-info',
					{ userId },
				);
				this.setTooltipTitle(failedMessage);
				Notification.error(failedMessage);
			},
		);
		this.setTooltipTitle(
			this.i18n.tr('modified-by.fetch-user-info', { userId }),
		);
	}
}
